/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { GetServerSideProps } from 'next';

import { PageRendererComponent, getProcessEnvs, prePageCall } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';

import {
  INVALID_OFFERS_PARAM,
  OFFERS_HUB_PATH,
  PARAM_PAGENO,
  hasErrorQueryFlag,
  processDynamicProps,
  scrubErrorTextFromModel,
  updateRobotMetaNames,
  updateDataLayer,
} from '@marriott/mi-offers-components';

import {
  mockOffersApiResponse,
  mockOffersApiResponseLightning,
  mockOffersCobrandSingleOfferApiResponse,
} from '../mock/mockOffersApiResponse';
import { fetchOffersSpecificData } from '@marriott/mi-offers-components';
import { isLocalDev } from '@marriott/mi-apollo-client-utils';
import OfferSignatures from '../signatures/offerSignatures.json';
import { NEXT_PUBLIC_ENV_KEYS } from '@marriott/mi-offers-components';
import { mockModel } from '../mock/mockModal';

const { LOCAL_UXL_CONTENT_URI } = process.env;

function pageRenderer(props: any) {
  if (isLocalDev) {
    console.log('[page] props - ', props);
  }

  const hasErrorFlag = hasErrorQueryFlag(props?.query ?? {});

  if (!hasErrorFlag) {
    scrubErrorTextFromModel(props?.model);
  }

  if (hasErrorFlag && props.model?.metaNames) {
    const mappedMetaNames = updateRobotMetaNames(props.model.metaNames);
    props.model['metaNames'] = mappedMetaNames;
  }

  if (
    !props.isAuthorMode &&
    props?.productSpecificResponse &&
    Object.keys(props.productSpecificResponse).length > 0 &&
    !isLocalDev
  ) {
    processDynamicProps(props);
  }

  console.log('canonical url', props.model['canonical']);
  const url =
    props.model && props.model['canonical'] !== undefined && props.model['canonical'] !== ''
      ? new URL(props.model['canonical'], props.model['canonical'])
      : ''; // Base URL needed to parse relative URL
  const param = PARAM_PAGENO;
  if (url !== '' && url.searchParams.has(param)) {
    if (props.pageNo === '1') {
      url.searchParams.delete(param);
    } else {
      url.searchParams.set(param, props.pageNo); // Update the parameter value
    }
  } else {
    if (url !== '' && props.pageNo != null && props.pageNo !== '1') {
      url.searchParams.append(param, props.pageNo); // Add it if it doesn't exist
    }
  }
  const baseUrl = url.toString();
  props.model['canonical'] = baseUrl;
  return <PageRendererComponent {...props} />;
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const offersSpecificData = async (context: any, parallelCallsResponse: any, interceptorResponse: any) => {
  const { req, res } = context;
  const { log } = global.loggerInstance('offersSpecificData');
  log.debug('[page] offers context -', context, 'context resolvedUrl -', context?.resolvedUrl, 'req -', req);
  log.debug('[page] parallelCallsResponse ', parallelCallsResponse);
  log.debug('[page] interceptorResponse ', interceptorResponse);

  const resolvedUrlString: string = req?.url;
  const isAuthorMode = resolvedUrlString.includes('isAuthorMode');
  const currentLocale = interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.replace(
    '_',
    '-'
  );

  let pageSpecificDatalayer = {};

  //get pageno param for renderer
  let pageNo = null;
  if (isAuthorMode == false) {
    const url = new URL(req.url, `http://${req.headers.host}`); // Use the full URL
    //capturing query param pageNO from req url
    pageNo = url.searchParams.get(PARAM_PAGENO);
  }

  const { ALLOWED_OFFER_LOCALES = '', GOOGLE_MAP_API_KEY } = getProcessEnvs();
  const allowedOfferLocales = ALLOWED_OFFER_LOCALES.split(',');

  if (!allowedOfferLocales.includes(currentLocale) && !isAuthorMode && !isLocalDev) {
    res.writeHead(301, { Location: `${OFFERS_HUB_PATH}${INVALID_OFFERS_PARAM}` });
    res.end();
    return null;
  }

  const [featureFlags, featureFlagLogo, featureFlagAemltsHost] = await Promise.all([
    // Feature flag call for MBOP
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_MBOP, {
      target: TARGETS.APP.OFFERS_RENDERER,
    }),
    // Feature flag call for logo automation
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
      target: TARGETS.APP.OFFERS_RENDERER,
    }),
    // Feature flag to switch AEM host to LTS host
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_OFFERS_RENDERER_AEMLTS_HOST, {}),
  ]);

  const useAemLtsHost = featureFlagAemltsHost?.experience_mcom_offers_renderer_aemlts_host;
  const aemHost = useAemLtsHost ? process.env.NEXT_PUBLIC_AEM_LTS_HOST : process.env.NEXT_PUBLIC_AEM_HOST;

  const getOfferDataFromLightning =
    context?.query?.lightning === true ||
    (featureFlags?.experience_mcom_mbop as { offer_details_switch?: boolean })?.offer_details_switch
      ? true
      : false;

  const isCobrandMockDataRequired = isAuthorMode && context?.resolvedUrl?.includes('dme-single-card');
  const offersSpecificData = isLocalDev
    ? mockOffersApiResponse
    : isAuthorMode
    ? isCobrandMockDataRequired
      ? mockOffersCobrandSingleOfferApiResponse
      : getOfferDataFromLightning
      ? mockOffersApiResponseLightning
      : mockOffersApiResponse
    : await fetchOffersSpecificData(context, currentLocale, featureFlags?.experience_mcom_mbop);
  log.debug('service api response::', offersSpecificData);
  let uri = offersSpecificData?.response?.uri;
  if (uri && isLocalDev && !isAuthorMode) {
    const resolvedOfferType = uri.split('offers-').pop().split('/')[0];
    uri = `${LOCAL_UXL_CONTENT_URI}${resolvedOfferType}`;
  } else if (typeof uri == 'undefined') {
    uri = null;
  }

  if (!isAuthorMode && offersSpecificData && Object.keys(offersSpecificData?.response)?.length && !isLocalDev) {
    pageSpecificDatalayer = updateDataLayer(offersSpecificData?.response);
  }

  return {
    operationSignatures: OfferSignatures,
    productSpecificResponse: offersSpecificData?.response,
    uri: uri,
    featureFlagAemltsHost: featureFlagAemltsHost ?? null,
    aemHost: aemHost ?? null,
    nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
    pageNo: pageNo,
    serverDataLayer: pageSpecificDatalayer,
    GOOGLE_MAP_API_KEY,
    featureFlags: { featureFlags, featureFlagLogo },
  };
};

const postAEMModelOperations = async (...args: any[]) => {
  const [, , , model] = args;
  const enableWebSDK = String(model?.enableWebSDK);
  if (enableWebSDK === 'true' && process.env['NEXT_PUBLIC_ENABLE_WEBSDK'] === 'true') {
    const ENABLE_CLIENT_DATA_LAYER = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_RTP_ENABLE_ACDL, {
      target: TARGETS.APP.OFFERS_RENDERER,
    });
    return {
      ...model,
      ENABLE_ACDL: ENABLE_CLIENT_DATA_LAYER?.experience_mcom_rtp_enable_acdl,
    };
  } else {
    return {
      ...model,
      ENABLE_ACDL: false,
    };
  }
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.

export const getServerSideProps: GetServerSideProps<any> = isLocalDev
  ? prePageCall(offersSpecificData, mockModel)
  : prePageCall(offersSpecificData, undefined, false, false, false, undefined, postAEMModelOperations);

export default pageRenderer;
